import { useEffect, useState } from 'react';
import moment from 'moment';
import { DateFilters } from 'shared/components/table-filter-chips/models/date-filters';

export type FilterChipDateTimeProps = {
  placeHolder: string;
  currentDateFilters: DateFilters;
  onDateFiltersChanged: (newDateFilters: DateFilters) => void;
  includeTime: boolean;
  onlyHistory: boolean;
};

type UseFilterChipDateTime = {
  isOpen: boolean;
  toggleIsOpen: () => void;
  setDatePickerIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dateFilters: DateFilters;
  setDateFilters: React.Dispatch<React.SetStateAction<DateFilters>>;
  isAnyDateFilterFilled: () => boolean;
  placeHolder: string;
  onDateFiltersChanged: (newDateFilters: DateFilters) => void;
  includeTime: boolean;
  onlyHistory: boolean;
  handleCancelClick: () => void;
  handleApplyClick: () => void;
  handleOutsideAlerter: () => void;
};

export const useFilterChipDateTime = ({
  placeHolder,
  currentDateFilters,
  onDateFiltersChanged,
  includeTime,
  onlyHistory,
}: Readonly<FilterChipDateTimeProps>): UseFilterChipDateTime => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleIsOpen = (): void => setIsOpen((prev) => !prev);
  const closeIsOpen = (): void => setIsOpen(false);

  const [datePickerIsOpen, setDatePickerIsOpen] = useState(false);

  const initialDateFilters: DateFilters = {
    dateFrom: currentDateFilters.dateFrom ? moment(currentDateFilters.dateFrom) : null,
    dateTo: currentDateFilters.dateTo ? moment(currentDateFilters.dateTo) : null,
  };

  const [dateFilters, setDateFilters] = useState<DateFilters>(initialDateFilters);

  useEffect(() => {
    setDateFilters(initialDateFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDateFilters]);

  const isAnyDateFilterFilled = (): boolean => dateFilters.dateFrom !== null || dateFilters.dateTo !== null;

  const validateDateFilters = (): boolean => {
    if (dateFilters.dateFrom != null && dateFilters.dateTo != null && dateFilters.dateFrom > dateFilters.dateTo) {
      return false;
    }

    if (!onlyHistory) {
      return true;
    }

    if (
      dateFilters.dateFrom != null &&
      (!dateFilters.dateFrom.isValid() ||
        dateFilters.dateFrom > moment() ||
        (dateFilters.dateTo != null && dateFilters.dateFrom > dateFilters.dateTo))
    ) {
      return false;
    }

    if (
      dateFilters.dateTo != null &&
      (!dateFilters.dateTo.isValid() ||
        dateFilters.dateTo > moment() ||
        (dateFilters.dateFrom != null && dateFilters.dateTo < dateFilters.dateFrom))
    ) {
      return false;
    }

    return true;
  };

  const handleCancelClick = (): void => {
    setDateFilters(initialDateFilters);
    closeIsOpen();
  };

  const handleApplyClick = (): void => {
    if (validateDateFilters()) {
      onDateFiltersChanged(dateFilters);
      closeIsOpen();
    }
  };

  const handleOutsideAlerter = (): void => {
    if (!datePickerIsOpen) {
      setDateFilters(initialDateFilters);
      closeIsOpen();
    }
  };

  return {
    isOpen,
    toggleIsOpen,
    setDatePickerIsOpen,
    dateFilters,
    setDateFilters,
    isAnyDateFilterFilled,
    placeHolder,
    onDateFiltersChanged,
    includeTime,
    onlyHistory,
    handleCancelClick,
    handleApplyClick,
    handleOutsideAlerter,
  };
};
